import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentShop,
  hasPermission,
  getShopOwnerCurrencySync,
  isOwnerInShopMode,
} from "@/lib/auth";
import {
  getShopSalesReport,
  getShopProductsReport,
  getShopCustomersReport,
  getShopDiscrepancies,
  getShopTransfersReport,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  Download,
  BarChart3,
  Users,
  Package,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Eye,
  Clock,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  exportSalesToExcel,
  exportTransfersToExcel,
  exportProductsToExcel,
  exportCustomersToExcel,
} from "@/lib/exportToExcel";
import {
  getTodayRange,
  getStartOfWeek,
  getEndOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  formatLocalDate,
} from "@/lib/utils/date-utils";
import { usePlan } from "@/hooks/usePlan";
import { getShopReturns } from "@/lib/api";
import { useDebounce } from "@/hooks/useDebounce";

// Función para obtener símbolo de moneda
const getCurrencySymbol = (): string => {
  const currency = getShopOwnerCurrencySync();
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    CUP: "$",
    L: "L",
    MXN: "$",
  };
  return symbols[currency] || "$";
};

// Función para formatear fecha con hora
const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return dateString;
  }
};

// Función para formatear fechas de discrepancias en formato 12h (AM/PM)
const formatDiscrepancyDate = (dateString: string): string => {
  try {
    // Para fechas en formato SQL "2026-03-11 19:17:44.845"
    const match = dateString.match(
      /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
    );
    if (match) {
      const [_, year, month, day, hour24, minute] = match;

      // Convertir hora de 24h a 12h con AM/PM
      const hour24Num = parseInt(hour24, 10);
      const period = hour24Num >= 12 ? "p.m." : "a.m.";
      let hour12 = hour24Num % 12;
      if (hour12 === 0) hour12 = 12; // 0 -> 12, 12 -> 12

      const hour12Str = hour12.toString().padStart(2, "0");

      return `${day}/${month}/${year} ${hour12Str}:${minute} ${period}`;
    }
    return dateString;
  } catch {
    return dateString;
  }
};

// Función para convertir dateRange a fechas concretas - SOLUCIÓN SIMPLE
const getDateFilters = (
  dateRange: string,
  startDate: string,
  endDate: string,
) => {
  const filters: { startDate?: string; endDate?: string } = {};

  switch (dateRange) {
    case "today":
      // Rango de HOY en hora Cuba
      const { start, end } = getTodayRange();
      filters.startDate = start.toISOString();
      filters.endDate = end.toISOString();
      break;

    case "week":
      const startOfWeek = getStartOfWeek();
      const endOfWeek = getEndOfWeek();
      filters.startDate = startOfWeek.toISOString();
      filters.endDate = endOfWeek.toISOString();
      break;

    case "month":
      const startOfMonth = getStartOfMonth();
      const endOfMonth = getEndOfMonth();
      filters.startDate = startOfMonth.toISOString();
      filters.endDate = endOfMonth.toISOString();
      break;

    case "custom":
      if (startDate && endDate) {
        // Fechas personalizadas en hora Cuba
        const customStart = new Date(startDate + "T00:00:00");
        const customEnd = new Date(endDate + "T23:59:59.999");

        filters.startDate = customStart.toISOString();
        filters.endDate = customEnd.toISOString();
      }
      break;

    default:
      break;
  }

  return filters;
};

export default function ShopReports() {
  const currentShop = getCurrentShop();
  const queryClient = useQueryClient();

  // ===== 1. TODOS LOS HOOKS BÁSICOS =====
  const { can } = usePlan();

  const [reportType, setReportType] = useState<
    "sales" | "products" | "customers" | "discrepancies" | "transfers"
  >("sales");
  const [dateRange, setDateRange] = useState<
    "today" | "week" | "month" | "custom"
  >("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [productView, setProductView] = useState<"quantity" | "revenue">(
    "quantity",
  );
  const [customerView, setCustomerView] = useState<
    "spent" | "frequent" | "inactive"
  >("spent");
  const [discrepancySearch, setDiscrepancySearch] = useState("");
  const [transferView, setTransferView] = useState<"all" | "byBank" | "byDate">(
    "all",
  );
  const [transferSearch, setTransferSearch] = useState("");
  const debouncedTransferSearch = useDebounce(transferSearch, 400);
  const [isExporting, setIsExporting] = useState(false);

  // ============ ESTADOS DE PAGINACIÓN PARA PRODUCTOS ============
  const [productsPage, setProductsPage] = useState(1);
  const [productsLimit] = useState(20); // 20 items por página

  // ============ ESTADOS DE PAGINACIÓN ============
  const [salesPage, setSalesPage] = useState(1);
  const [salesLimit] = useState(20); // Límite fijo por ahora

  // Paginación Transferencias
  const [transfersPage, setTransfersPage] = useState(1);
  const [transfersLimit] = useState(20); // Límite fijo para transferencias

  // ============ ESTADO PARA DEVOLUCIONES ============
  const [returnsData, setReturnsData] = useState<any[]>([]);
  const [isLoadingReturns, setIsLoadingReturns] = useState(false);

  // Cargar devoluciones cuando cambian las fechas o la tienda
  useEffect(() => {
    if (currentShop?.id) {
      loadReturnsForPeriod();
    }
  }, [currentShop?.id, dateRange, startDate, endDate]); // ✅ Usamos las variables reales

  const loadReturnsForPeriod = async () => {
    // Solo cargar si tenemos fechas válidas (para custom) o si es un rango predefinido
    if (dateRange === "custom" && (!startDate || !endDate)) {
      return;
    }

    setIsLoadingReturns(true);
    try {
      // Convertir fechas al formato que espera el backend (ISO string)
      let startIso: string | undefined;
      let endIso: string | undefined;

      if (dateRange === "custom" && startDate && endDate) {
        // Fechas personalizadas: convertir a ISO con hora completa
        startIso = new Date(startDate + "T00:00:00").toISOString();
        endIso = new Date(endDate + "T23:59:59.999").toISOString();
      }
      // Para "today", "week", "month" el backend ya maneja los rangos por defecto
      // así que podemos enviar undefined y que use los valores por defecto

      const result = await getShopReturns(currentShop!.id, {
        startDate: startIso,
        endDate: endIso,
      });
      if (result.success) {
        setReturnsData(result.data || []);
      }
    } catch (error) {
      console.warn("No se pudieron cargar devoluciones:", error);
    } finally {
      setIsLoadingReturns(false);
    }
  };

  // ===== 2. DETECCIÓN DE OWNER EN MODO TIENDA =====
  const isOwnerMode = isOwnerInShopMode();

  // ===== 3. VERIFICACIÓN DE ACCESO CON SOPORTE PARA OWNER (RESPETANDO PLAN) =====
  if (!currentShop) return null;

  // El owner tiene acceso, pero sujeto a las limitaciones del plan
  // FORZAMOS LA EVALUACIÓN CORRECTA USANDO UNA VARIABLE INTERMEDIA
  const hasPermissionOrOwner = isOwnerMode || hasPermission("store_manager");
  const hasAccess = hasPermissionOrOwner && can.useReports();

  // ===== 3. HOOKS CONDICIONALES - USAR FLAGS =====
  // Habilitar/deshabilitar queries según el acceso
  const queriesEnabled = hasAccess && !!currentShop;

  // ===== 4. TODAS LAS CONSULTAS useQuery - SIEMPRE EN EL MISMO ORDEN =====
  const { data: salesReport, isLoading: salesLoading } = useQuery({
    queryKey: [
      "shop-sales-report",
      currentShop?.id,
      dateRange,
      startDate,
      endDate,
      salesPage, // ← NUEVO: Recargar cuando cambia la página
    ],
    queryFn: () => {
      const filters = getDateFilters(dateRange, startDate, endDate);
      return getShopSalesReport(
        currentShop!.id,
        filters,
        salesPage, // ← Pasar página actual
        salesLimit, // ← Pasar límite
      );
    },
    enabled:
      (isOwnerMode || (hasPermission("store_manager") && can.useReports())) &&
      reportType === "sales",
  });

  const { data: productsReport, isLoading: productsLoading } = useQuery({
    queryKey: [
      "shop-products-report",
      currentShop?.id,
      dateRange,
      startDate,
      endDate,
      productsPage,
    ],
    queryFn: () => {
      const filters = getDateFilters(dateRange, startDate, endDate);
      return getShopProductsReport(
        currentShop!.id,
        filters,
        productsPage, // ← Pasar página actual
        productsLimit, // ← Pasar límite (20)
      );
    },
    enabled:
      (isOwnerMode || (hasPermission("store_manager") && can.useReports())) &&
      reportType === "products", // ← MODIFICADO
  });

  const { data: customersReport, isLoading: customersLoading } = useQuery({
    queryKey: [
      "shop-customers-report",
      currentShop?.id,
      dateRange,
      startDate,
      endDate,
    ],
    queryFn: () => {
      const filters = getDateFilters(dateRange, startDate, endDate);
      return getShopCustomersReport(currentShop!.id, filters);
    },
    enabled:
      (isOwnerMode || (hasPermission("store_manager") && can.useReports())) &&
      reportType === "customers", // ← MODIFICADO
  });

  const { data: discrepanciesReport, isLoading: discrepanciesLoading } =
    useQuery({
      queryKey: [
        "shop-discrepancies-report",
        currentShop?.id,
        dateRange,
        startDate,
        endDate,
        discrepancySearch,
      ],
      queryFn: () => {
        const filters = getDateFilters(dateRange, startDate, endDate);
        return getShopDiscrepancies(currentShop!.id, {
          startDate: filters.startDate,
          endDate: filters.endDate,
          search: discrepancySearch,
        });
      },
      enabled:
        (isOwnerMode || (hasPermission("store_manager") && can.useReports())) &&
        reportType === "discrepancies", // ← MODIFICADO
    });

  // {esta sección configura la consulta de datos para el reporte de transferencias}
  const { data: transfersReport, isLoading: transfersLoading } = useQuery({
    queryKey: [
      "shop-transfers-report",
      currentShop?.id,
      dateRange,
      startDate,
      endDate,
      transfersPage,
      transfersLimit,
      transferView, // Importante para el ordenamiento global
      debouncedTransferSearch,
    ],
    queryFn: () => {
      const filters = getDateFilters(dateRange, startDate, endDate);
      return getShopTransfersReport(currentShop!.id, {
        ...filters,
        page: transfersPage,
        limit: transfersLimit,
        transferView: transferView,
        search: debouncedTransferSearch, // Pasamos el string directo
      });
    },
    enabled: hasAccess && !!currentShop && reportType === "transfers",
  });

  // ===== 5. RETURN CONDICIONAL (DESPUÉS DE TODOS LOS HOOKS) =====
  if (!hasAccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground">
              {!can.useReports()
                ? "Los reportes requieren un plan Profesional o Enterprise."
                : "No tienes permiso para acceder a los reportes."}
            </p>
            {!can.useReports() && (
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/owner/plans")}
                className="mt-4 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
              >
                Mejorar Plan
              </Button>
            )}
            {can.useReports() && !isOwnerMode && (
              <p className="text-sm mt-4 text-muted-foreground">
                Necesitas permisos de manager o administrador para ver reportes.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== 6. RESTO DEL CÓDIGO (cálculos, etc.) =====
  const isLoading =
    (reportType === "sales" && salesLoading) ||
    (reportType === "products" && productsLoading) ||
    (reportType === "customers" && customersLoading) ||
    (reportType === "discrepancies" && discrepanciesLoading) ||
    (reportType === "transfers" && transfersLoading);

  const salesData = salesReport?.data?.data || [];
  const rawSummary = salesReport?.data?.summary || {};
  const salesSummary = {
    totalSales: Number(rawSummary.totalSales) || 0,
    totalRevenue: Number(rawSummary.totalRevenue) || 0,
    averageTicket: Number(rawSummary.averageTicket) || 0,
    // Incluir paymentMethods si existe, sino objeto vacío seguro
    paymentMethods: rawSummary.paymentMethods || {
      efectivo: 0,
      transferencia: 0,
    },
  };
  const salesTotal = salesReport?.data?.total || 0; // ← Total real de la BD
  const salesTotalPages = Math.ceil(salesTotal / salesLimit);
  // Calcular métricas de devoluciones y ventas netas
  const totalReturns = returnsData.reduce(
    (sum, ret) => sum + parseFloat(ret.totalAmount || "0"),
    0,
  );
  const grossRevenue = salesSummary?.totalRevenue || 0;
  const grossSalesCount = salesSummary?.totalSales || 0;

  const netRevenue = grossRevenue - totalReturns;
  const netSalesCount = Math.max(0, grossSalesCount - returnsData.length);
  const productsData = productsReport?.data?.data || [];
  const productsSummary = productsReport?.data?.summary || {
    totalProducts: 0,
    totalUnits: 0,
    totalRevenue: 0,
  };
  const productsTotal = productsReport?.data?.total || 0; // ← NUEVO: Total real de la BD
  const productsTotalPages = Math.ceil(productsTotal / productsLimit);
  const customersData = customersReport?.data?.data || [];
  const customersSummary = customersReport?.data?.summary || {
    totalCustomers: 0,
    activeCustomers: 0,
    totalSpent: 0,
  };
  const discrepanciesData = discrepanciesReport?.data?.data || [];
  const discrepanciesSummary = discrepanciesReport?.data?.summary || {
    totalDiscrepancies: 0,
    totalAmount: 0,
    promedioFaltante: 0,
    faltantes: 0,
    sobrantes: 0,
    totalFaltantes: 0,
    totalSobrantes: 0,
  };

  // Datos de transferencias (NUEVOS)
  const transfersData = transfersReport?.transfers || [];
  const transfersTotal = transfersReport?.total || 0; // ← NUEVO: Total para paginación
  const transfersTotalPages = Math.ceil(transfersTotal / transfersLimit);
  const transfersSummary = transfersReport?.summary || {
    count: 0,
    totalAmount: 0,
    bankStats: {},
  };

  // Función para exportar discrepancias a CSV
  const exportDiscrepanciesToCSV = (data: any[]) => {
    const headers = [
      "Fecha",
      "Caja",
      "Cajero",
      "Efectivo Esperado",
      "Efectivo Contado",
      "Diferencia",
      "Tipo",
      "Explicación",
    ].join(",");

    const rows = data.map((d) =>
      [
        new Date(d.createdAt).toISOString(),
        `"${d.cashRegisterName}"`,
        `"${d.closedBy}"`,
        d.expectedCash,
        d.countedCash,
        d.cashDifference,
        d.discrepancyType,
        d.notes ? `"${d.notes.replace(/"/g, '""')}"` : "Sin explicación",
      ].join(","),
    );

    const csvContent = [headers, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `discrepancias_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para exportar a Excel
  const handleExport = async () => {
    try {
      setIsExporting(true);
      switch (reportType) {
        case "sales": {
          // Obtener TODAS las ventas del rango de tiempo seleccionado (modo exportaciÃ³n)
          const filters = getDateFilters(dateRange, startDate, endDate);

          // Mostrar progreso inicial
          toast({
            title: "Exportando",
            description: `Obteniendo todas las ventas del rango...`,
          });

          // Llamar al backend en modo exportaciÃ³n (sin paginaciÃ³n)
          const response = await getShopSalesReport(
            currentShop!.id,
            filters,
            1,
            20,
            true, // forExport = true
          );

          const allSalesData = response.data?.data || [];

          if (allSalesData.length === 0) {
            toast({
              title: "Sin datos",
              description: "No hay ventas para exportar",
              variant: "destructive",
            });
            return;
          }

          await exportSalesToExcel(allSalesData, dateRange);
          toast({
            title: "Exportado",
            description: `${allSalesData.length} ventas exportadas a Excel correctamente`,
          });
          break;
        }
        case "products": {
          const filters = getDateFilters(dateRange, startDate, endDate);

          toast({
            title: "Exportando",
            description: "Obteniendo todos los productos...",
          });

          // Llamar al backend en modo exportación (sin paginación)
          const response = await getShopProductsReport(
            currentShop!.id,
            filters,
            1,
            20,
            true, // forExport = true
          );

          const allProductsData = response.data?.data || [];

          if (allProductsData.length === 0) {
            toast({
              title: "Sin datos",
              description: "No hay productos para exportar",
              variant: "destructive",
            });
            return;
          }

          await exportProductsToExcel(allProductsData, productView);
          toast({
            title: "Exportado",
            description: `${allProductsData.length} productos exportados a Excel correctamente`,
          });
          break;
        }
        case "customers": {
          const filters = getDateFilters(dateRange, startDate, endDate);

          toast({
            title: "Exportando",
            description: "Obteniendo todos los clientes...",
          });

          // Llamar al backend en modo exportación (sin paginación)
          const response = await getShopCustomersReport(
            currentShop!.id,
            filters,
            1,
            20,
            true, // forExport = true
          );

          const allCustomersData = response.data?.data || [];

          if (allCustomersData.length === 0) {
            toast({
              title: "Sin datos",
              description: "No hay clientes para exportar",
              variant: "destructive",
            });
            return;
          }

          await exportCustomersToExcel(allCustomersData, customerView);
          toast({
            title: "Exportado",
            description: `${allCustomersData.length} clientes exportados a Excel correctamente`,
          });
          break;
        }
        case "discrepancies": {
          const filters = getDateFilters(dateRange, startDate, endDate);

          toast({
            title: "Exportando",
            description: "Obteniendo todas las discrepancias...",
          });

          // Llamar al backend en modo exportación (sin paginación)
          const response = await getShopDiscrepancies(
            currentShop!.id,
            { ...filters, search: discrepancySearch },
            1,
            20,
            true, // forExport = true
          );

          const allDiscrepanciesData = response.data?.data || [];

          if (allDiscrepanciesData.length === 0) {
            toast({
              title: "Sin datos",
              description: "No hay discrepancias para exportar",
              variant: "destructive",
            });
            return;
          }

          exportDiscrepanciesToCSV(allDiscrepanciesData);
          toast({
            title: "Exportado",
            description: `${allDiscrepanciesData.length} discrepancias exportadas correctamente`,
          });
          break;
        }
        case "transfers": {
          const filters = getDateFilters(dateRange, startDate, endDate);

          toast({
            title: "Exportando",
            description: "Obteniendo todas las transferencias...",
          });

          // Llamar al backend en modo exportación (sin paginación)
          const response = await getShopTransfersReport(currentShop!.id, {
            ...filters,
            search:
              transferView === "all" ? debouncedTransferSearch : undefined,
            transferView: transferView,
            forExport: true, // Modo exportación
          });

          const allTransfersData = response.transfers || [];

          if (allTransfersData.length === 0) {
            toast({
              title: "Sin datos",
              description: "No hay transferencias para exportar",
              variant: "destructive",
            });
            return;
          }

          await exportTransfersToExcel(allTransfersData, dateRange);
          toast({
            title: "Exportado",
            description: `${allTransfersData.length} transferencias exportadas a Excel correctamente`,
          });
          break;
        }
      }
    } catch (error) {
      console.error("Error exportando a Excel:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar a Excel",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (!currentShop) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Encabezado */}
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase text-primary">
            Reportes y Análisis
          </h1>
          <p className="text-muted-foreground">
            Analiza el desempeño de tu negocio con reportes detallados
          </p>
        </div>

        {/* Botón de actualizar */}
        {/* Botón de actualizar */}
        <Button
          variant="outline"
          onClick={() => {
            // Invalidar TODAS las queries de reportes
            queryClient.invalidateQueries({ queryKey: ["shop-sales-report"] });
            queryClient.invalidateQueries({
              queryKey: ["shop-products-report"],
            });
            queryClient.invalidateQueries({
              queryKey: ["shop-customers-report"],
            });
            queryClient.invalidateQueries({
              queryKey: ["shop-discrepancies-report"],
            });
            queryClient.invalidateQueries({
              queryKey: ["shop-transfers-report"],
            });

            toast({
              title: "Actualizando",
              description: "Recargando datos de reportes...",
            });
          }}
          disabled={isLoading}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          {isLoading ? "Actualizando..." : "Actualizar"}
        </Button>
      </div>

      {/* Controles de reporte */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Tipo de reporte */}
            <div>
              <Label>Tipo de Reporte</Label>
              <Select
                value={reportType}
                onValueChange={(v: any) => setReportType(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar reporte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Ventas
                    </div>
                  </SelectItem>
                  <SelectItem value="products">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Productos
                    </div>
                  </SelectItem>
                  <SelectItem value="customers">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Clientes
                    </div>
                  </SelectItem>
                  <SelectItem value="discrepancies">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Discrepancias
                    </div>
                  </SelectItem>
                  {/* Nueva opción para transferencias */}
                  <SelectItem value="transfers">
                    <div className="flex items-center gap-2">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                        ></path>
                      </svg>
                      Transferencias
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Rango de fechas */}
            <div>
              <Label>Rango de Fechas</Label>
              <Select
                value={dateRange}
                onValueChange={(v: any) => setDateRange(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Rango de fechas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="week">Esta semana</SelectItem>
                  <SelectItem value="month">Este mes</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fechas personalizadas */}
            {dateRange === "custom" && (
              <>
                <div>
                  <Label>Fecha inicio</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Fecha fin</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Botón exportar */}
            <div className="flex items-end">
              <Button
                onClick={handleExport}
                className="w-full gap-2"
                disabled={isLoading || isExporting}
              >
                <Download className="h-4 w-4" />
                {isExporting ? "Exportando..." : "Exportar Excel"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reporte de Ventas */}
      {reportType === "sales" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Reporte de Ventas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : salesData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p className="text-lg">No hay ventas registradas</p>
                <p className="text-sm">Realiza ventas para ver reportes</p>
              </div>
            ) : (
              <>
                {/* Resumen estadístico */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Total Ventas
                      </p>
                      <p className="text-2xl font-bold">
                        {salesSummary.totalSales || 0}
                      </p>
                    </CardContent>
                  </Card>
                  {/* Tarjeta de Ventas Netas */}
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Ventas Netas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-emerald-900">
                        {new Intl.NumberFormat("es-CU", {
                          style: "currency",
                          currency: getShopOwnerCurrencySync(), // ✅ CORREGIDO: Usar función existente
                        }).format(netRevenue)}
                      </div>
                      <p className="text-xs text-emerald-600 mt-1">
                        {netSalesCount} transacciones
                        {totalReturns > 0 && (
                          <span className="ml-1 block mt-1 text-emerald-500/80 text-[10px]">
                            (Brutas:{" "}
                            {new Intl.NumberFormat("es-CU", {
                              style: "currency",
                              currency: getShopOwnerCurrencySync(), // ✅ CORREGIDO
                            }).format(grossRevenue)}{" "}
                            − Dev:{" "}
                            {new Intl.NumberFormat("es-CU", {
                              style: "currency",
                              currency: getShopOwnerCurrencySync(), // ✅ CORREGIDO
                            }).format(totalReturns)}
                            )
                          </span>
                        )}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Ingresos Totales
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {getCurrencySymbol()}
                        {salesSummary.totalRevenue?.toFixed(2) || "0.00"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Promedio por Venta
                      </p>
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol()}
                        {Number(salesSummary.averageTicket || 0).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Método más usado
                      </p>
                      <p className="text-2xl font-bold">
                        {(() => {
                          const pm = salesSummary.paymentMethods || {};
                          const efectivo = Number(pm.efectivo) || 0;
                          const transferencia = Number(pm.transferencia) || 0;
                          return efectivo > transferencia
                            ? "Efectivo"
                            : "Transferencia";
                        })()}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tabla de ventas */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Producto(s)</TableHead>
                        <TableHead>Método de Pago</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesData.slice(0, 20).map((sale: any) => (
                        <TableRow key={sale.id}>
                          <TableCell>
                            {new Date(
                              sale.createdAt || sale.date,
                            ).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <div
                              className="truncate"
                              title={sale.products || "N/A"}
                            >
                              {sale.products || "N/A"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                sale.paymentMethod === "efectivo"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {sale.paymentMethod}
                            </span>
                          </TableCell>
                          <TableCell className="font-bold">
                            {getCurrencySymbol()}
                            {Number(sale.total || sale.amount || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Paginación de Ventas */}
                {salesTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {salesData.length} de {salesTotal} resultados
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSalesPage((p) => Math.max(1, p - 1))}
                        disabled={salesPage === 1}
                      >
                        Anterior
                      </Button>
                      <div className="flex items-center px-2 text-sm">
                        Página {salesPage} de {salesTotalPages}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSalesPage((p) => Math.min(salesTotalPages, p + 1))
                        }
                        disabled={salesPage === salesTotalPages}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reporte de Productos */}
      {reportType === "products" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Reporte de Productos
              </div>
              <div className="flex gap-2">
                <Select
                  value={productView}
                  onValueChange={(v: any) => setProductView(v)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Ver por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quantity">
                      Más Vendidos (cantidad)
                    </SelectItem>
                    <SelectItem value="revenue">
                      Más Rentables (ingresos)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : productsData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p className="text-lg">No hay productos vendidos</p>
                <p className="text-sm">
                  Realiza ventas para ver estadísticas de productos
                </p>
              </div>
            ) : (
              <>
                {/* Estadísticas de productos */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Productos Vendidos
                      </p>
                      <p className="text-2xl font-bold">
                        {productsSummary.totalProducts || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Unidades Totales
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {productsSummary.totalUnits || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Ingresos Totales
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {getCurrencySymbol()}
                        {Number(productsSummary.totalRevenue || 0).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Promedio por Producto
                      </p>
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol()}
                        {(
                          (productsSummary.totalRevenue || 0) /
                          (productsSummary.totalProducts || 1)
                        ).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tabla de productos */}
                {/* Tabla de productos CON SCROLL INTERNO */}
                <div className="border rounded-lg">
                  <div className="h-[550px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Producto</TableHead>
                          <TableHead>Categoría</TableHead>
                          <TableHead className="text-right">
                            Unidades Vendidas
                          </TableHead>
                          <TableHead className="text-right">Ingresos</TableHead>
                          <TableHead className="text-right">Ventas</TableHead>
                          <TableHead className="text-right">
                            Promedio por Venta
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productsData
                          .sort((a: any, b: any) => {
                            if (productView === "quantity") {
                              return (
                                Number(b.totalQuantity || 0) -
                                Number(a.totalQuantity || 0)
                              );
                            } else {
                              return (
                                Number(b.totalRevenue || 0) -
                                Number(a.totalRevenue || 0)
                              );
                            }
                          })
                          // ← ELIMINADO: .slice(0, 20) porque el backend ya paga
                          .map((product: any, index: number) => (
                            <TableRow key={product.productId || product.id}>
                              <TableCell className="font-bold">
                                <div
                                  className={`flex items-center justify-center h-8 w-8 rounded-full ${
                                    index < 3
                                      ? "bg-primary/10 text-primary"
                                      : "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  {/* Ranking continuo entre páginas */}
                                  {index +
                                    1 +
                                    (productsPage - 1) * productsLimit}
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">
                                {product.productName || product.name}
                              </TableCell>
                              <TableCell>
                                <span className="px-2 py-1 bg-muted rounded text-xs">
                                  {product.category || "Sin categoría"}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {Number(product.totalQuantity || 0)}
                                {productView === "quantity" && (
                                  <div className="w-full bg-muted rounded-full h-2 mt-1">
                                    <div
                                      className="bg-primary h-2 rounded-full"
                                      style={{
                                        width: `${
                                          (Number(product.totalQuantity || 0) /
                                            Math.max(
                                              ...productsData.map((p: any) =>
                                                Number(p.totalQuantity || 0),
                                              ),
                                            )) *
                                          100
                                        }%`,
                                      }}
                                    />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-bold text-green-600">
                                {getCurrencySymbol()}
                                {Number(product.totalRevenue || 0).toFixed(2)}
                                {productView === "revenue" && (
                                  <div className="w-full bg-muted rounded-full h-2 mt-1">
                                    <div
                                      className="bg-green-500 h-2 rounded-full"
                                      style={{
                                        width: `${
                                          (Number(product.totalRevenue || 0) /
                                            Math.max(
                                              ...productsData.map((p: any) =>
                                                Number(p.totalRevenue || 0),
                                              ),
                                            )) *
                                          100
                                        }%`,
                                      }}
                                    />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {product.saleCount || 0}
                              </TableCell>
                              <TableCell className="text-right">
                                {getCurrencySymbol()}
                                {(
                                  Number(product.totalRevenue || 0) /
                                  (product.saleCount || 1)
                                ).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}
            {/* Paginación de Productos */}
            {productsTotalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Mostrando {productsData.length} de {productsTotal} resultados
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProductsPage((p) => Math.max(1, p - 1))}
                    disabled={productsPage === 1}
                  >
                    Anterior
                  </Button>
                  <div className="flex items-center px-2 text-sm">
                    Página {productsPage} de {productsTotalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setProductsPage((p) =>
                        Math.min(productsTotalPages, p + 1),
                      )
                    }
                    disabled={productsPage === productsTotalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reporte de Clientes */}
      {reportType === "customers" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Reporte de Clientes
              </div>
              <div className="flex gap-2">
                <Select
                  value={customerView}
                  onValueChange={(v: any) => setCustomerView(v)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Ver por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spent">Más Valiosos (gasto)</SelectItem>
                    <SelectItem value="frequent">Más Frecuentes</SelectItem>
                    <SelectItem value="inactive">Clientes Inactivos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : customersData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p className="text-lg">No hay clientes registrados</p>
                <p className="text-sm">
                  Registra clientes para ver estadísticas
                </p>
              </div>
            ) : (
              <>
                {/* Estadísticas de clientes */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Total Clientes
                      </p>
                      <p className="text-2xl font-bold">
                        {customersSummary.totalCustomers || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Clientes Activos
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {customersSummary.activeCustomers || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Ingresos de Clientes
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {getCurrencySymbol()}
                        {Number(customersSummary.totalSpent || 0).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Promedio por Cliente
                      </p>
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol()}
                        {(
                          (customersSummary.totalSpent || 0) /
                          (customersSummary.totalCustomers || 1)
                        ).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tabla de clientes */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">#</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>CI</TableHead>
                        <TableHead>Contacto</TableHead>
                        <TableHead className="text-right">Compras</TableHead>
                        <TableHead className="text-right">
                          Total Gastado
                        </TableHead>
                        <TableHead className="text-right">Promedio</TableHead>
                        <TableHead className="text-right">
                          Última Compra
                        </TableHead>
                        <TableHead className="text-right">Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customersData
                        .filter((customer: any) => {
                          if (customerView === "inactive") {
                            return customer.daysSinceLastPurchase > 30;
                          }
                          return true;
                        })
                        .sort((a: any, b: any) => {
                          if (customerView === "spent") {
                            return b.totalSpent - a.totalSpent;
                          } else if (customerView === "frequent") {
                            return b.totalPurchases - a.totalPurchases;
                          } else {
                            return (
                              b.daysSinceLastPurchase - a.daysSinceLastPurchase
                            );
                          }
                        })
                        .slice(0, 20)
                        .map((customer: any, index: number) => (
                          <TableRow key={customer.id}>
                            <TableCell className="font-bold">
                              <div
                                className={`flex items-center justify-center h-8 w-8 rounded-full ${
                                  index < 3
                                    ? "bg-primary/10 text-primary"
                                    : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {index + 1}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              {customer.fullName}
                              {customer.email && (
                                <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                                  {customer.email}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {customer.ci}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">{customer.phone}</div>
                            </TableCell>
                            <TableCell className="text-right font-bold">
                              {customer.totalPurchases}
                              <div className="w-full bg-muted rounded-full h-2 mt-1">
                                <div
                                  className="bg-blue-500 h-2 rounded-full"
                                  style={{
                                    width: `${(customer.totalPurchases / Math.max(...customersData.map((c: any) => c.totalPurchases))) * 100}%`,
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-bold text-green-600">
                              {getCurrencySymbol()}
                              {customer.totalSpent.toFixed(2)}
                              <div className="w-full bg-muted rounded-full h-2 mt-1">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{
                                    width: `${(customer.totalSpent / Math.max(...customersData.map((c: any) => c.totalSpent))) * 100}%`,
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {getCurrencySymbol()}
                              {customer.averagePurchase.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              {customer.lastPurchaseAt ? (
                                <div>
                                  <div className="text-sm">
                                    {new Date(
                                      customer.lastPurchaseAt,
                                    ).toLocaleDateString()}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {customer.daysSinceLastPurchase} días
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">
                                  Nunca
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  customer.daysSinceLastPurchase <= 7
                                    ? "bg-green-100 text-green-800"
                                    : customer.daysSinceLastPurchase <= 30
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                }`}
                              >
                                {customer.daysSinceLastPurchase <= 7
                                  ? "Activo"
                                  : customer.daysSinceLastPurchase <= 30
                                    ? "Regular"
                                    : "Inactivo"}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reporte de Discrepancias */}
      {reportType === "discrepancies" && (
        <Card>
          <CardHeader>
            {/* Título centrado en móvil */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2 md:mb-0">
                  <AlertTriangle className="h-5 w-5" />
                  <CardTitle className="text-center md:text-left">
                    Reporte de Discrepancias de Caja
                  </CardTitle>
                </div>
              </div>

              {/* Controles de búsqueda - debajo en móvil, al lado en desktop */}
              <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
                <Input
                  placeholder="Buscar por caja, cajero o explicación..."
                  value={discrepancySearch}
                  onChange={(e) => setDiscrepancySearch(e.target.value)}
                  className="w-full md:w-[250px]"
                />
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      queryClient.invalidateQueries({
                        queryKey: ["shop-discrepancies-report"],
                      });
                    }}
                    disabled={isLoading}
                    className="w-full sm:w-auto h-9"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                    />
                    <span className="ml-2 hidden sm:inline">Actualizar</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : discrepanciesData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p className="text-lg">No hay discrepancias registradas</p>
                <p className="text-sm">
                  Todas las operaciones de caja están correctas
                </p>
              </div>
            ) : (
              <>
                {/* Estadísticas de discrepancias */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  {/* Tarjeta de período */}
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Período</p>
                      <p className="text-xl font-bold">
                        {dateRange === "today"
                          ? "Hoy"
                          : dateRange === "week"
                            ? "Esta semana"
                            : dateRange === "month"
                              ? "Este mes"
                              : "Personalizado"}
                      </p>
                      {dateRange === "custom" && startDate && endDate ? (
                        <p className="text-sm text-muted-foreground mt-1">
                          {startDate} a {endDate}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1">
                          {discrepanciesData.length} registro(s)
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Total Discrepancias
                      </p>
                      <p className="text-2xl font-bold">
                        {discrepanciesSummary.totalDiscrepancies || 0}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Faltantes</p>
                      <p className="text-2xl font-bold text-destructive">
                        {discrepanciesSummary.faltantes || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Total: {getCurrencySymbol()}
                        {discrepanciesSummary.totalFaltantes?.toFixed(2) ||
                          "0.00"}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Sobrantes</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {discrepanciesSummary.sobrantes || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Total: {getCurrencySymbol()}
                        {discrepanciesSummary.totalSobrantes?.toFixed(2) ||
                          "0.00"}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Promedio Faltante
                      </p>
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol()}
                        {discrepanciesSummary.promedioFaltante?.toFixed(2) ||
                          "0.00"}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Indicador de vista */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {discrepanciesData.length} discrepancia(s) encontrada(s)
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="hidden md:flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Vista tabla
                      </span>
                      <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                    </div>
                    <div className="flex md:hidden items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Vista compacta
                      </span>
                      <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                </div>

                {/* Tabla de discrepancias - Responsive */}
                <div className="overflow-x-auto">
                  {/* Versión desktop (hidden en móvil) */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Caja</TableHead>
                          <TableHead>Cajero</TableHead>
                          <TableHead className="text-right">
                            Esperado / Contado
                          </TableHead>
                          <TableHead className="text-right">
                            Diferencia
                          </TableHead>
                          <TableHead>Explicación</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discrepanciesData.map((discrepancy: any) => (
                          <TableRow
                            key={discrepancy.id}
                            className="hover:bg-muted/50"
                            aria-label={`Discrepancia del ${new Date(discrepancy.createdAt).toLocaleDateString()} - ${discrepancy.discrepancyType} de ${getCurrencySymbol()}${Math.abs(parseFloat(discrepancy.cashDifference)).toFixed(2)}`}
                          >
                            <TableCell>
                              {formatDiscrepancyDate(discrepancy.createdAt)}
                            </TableCell>
                            <TableCell className="font-medium">
                              {discrepancy.cashRegisterName}
                            </TableCell>
                            <TableCell>{discrepancy.closedBy}</TableCell>
                            <TableCell className="text-right">
                              <div className="space-y-1">
                                <div className="text-sm">
                                  <span className="text-muted-foreground">
                                    Esperado:
                                  </span>{" "}
                                  {getCurrencySymbol()}
                                  {parseFloat(discrepancy.expectedCash).toFixed(
                                    2,
                                  )}
                                </div>
                                <div className="text-sm">
                                  <span className="text-muted-foreground">
                                    Contado:
                                  </span>{" "}
                                  {getCurrencySymbol()}
                                  {parseFloat(discrepancy.countedCash).toFixed(
                                    2,
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-col items-end">
                                <span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                    discrepancy.discrepancyType === "faltante"
                                      ? "bg-destructive/10 text-destructive border border-destructive/20"
                                      : "bg-amber-50 text-amber-700 border border-amber-200"
                                  }`}
                                >
                                  {discrepancy.discrepancyType === "faltante"
                                    ? "−"
                                    : "+"}
                                  {getCurrencySymbol()}
                                  {Math.abs(
                                    parseFloat(discrepancy.cashDifference),
                                  ).toFixed(2)}
                                </span>
                                <span className="text-xs text-muted-foreground mt-1">
                                  {discrepancy.discrepancyType === "faltante"
                                    ? "Faltante"
                                    : "Sobrante"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[250px]">
                              {discrepancy.notes ? (
                                <div className="group relative">
                                  <p
                                    className="text-sm line-clamp-2 cursor-help"
                                    title={discrepancy.notes}
                                  >
                                    {discrepancy.notes}
                                  </p>
                                  {discrepancy.notes.length > 50 && (
                                    <div className="absolute z-10 invisible group-hover:visible bg-popover text-popover-foreground p-2 rounded-md shadow-lg border text-xs max-w-xs mt-1">
                                      {discrepancy.notes}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <span className="text-sm text-muted-foreground italic">
                                    Sin explicación
                                  </span>
                                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                                    ⚠️
                                  </span>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Versión móvil (solo visible en móvil) */}
                  <div className="md:hidden space-y-4">
                    {discrepanciesData.map((discrepancy: any) => (
                      <Card
                        key={discrepancy.id}
                        className="overflow-hidden border-l-4 border-l-destructive/30"
                        role="article"
                        aria-labelledby={`discrepancy-${discrepancy.id}-title`}
                      >
                        <CardContent className="p-4">
                          {/* Header de la card con icono */}
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-start gap-2">
                              <div className="mt-1 p-1.5 rounded-full bg-destructive/10 text-destructive">
                                <AlertTriangle className="h-3.5 w-3.5" />
                              </div>
                              <div>
                                <div
                                  id={`discrepancy-${discrepancy.id}-title`}
                                  className="font-medium text-sm"
                                >
                                  {formatDiscrepancyDate(discrepancy.createdAt)}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400"></span>
                                  Caja: {discrepancy.cashRegisterName}
                                </div>
                              </div>
                            </div>
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/20">
                              {discrepancy.discrepancyType === "faltante"
                                ? "−"
                                : "+"}
                              {getCurrencySymbol()}
                              {Math.abs(
                                parseFloat(discrepancy.cashDifference),
                              ).toFixed(2)}
                            </div>
                          </div>

                          {/* Información principal */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="space-y-1">
                              <div className="text-xs text-muted-foreground">
                                Cajero
                              </div>
                              <div className="text-sm font-medium">
                                {discrepancy.closedBy}
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="text-xs text-muted-foreground">
                                Tipo
                              </div>
                              <div className="text-sm font-medium">
                                {discrepancy.discrepancyType === "faltante"
                                  ? "Faltante"
                                  : "Sobrante"}
                              </div>
                            </div>
                          </div>

                          {/* Montos */}
                          <div className="grid grid-cols-2 gap-3 mb-3 p-3 bg-muted/30 rounded-lg">
                            <div className="space-y-1">
                              <div className="text-xs text-muted-foreground">
                                Esperado
                              </div>
                              <div className="text-sm font-medium">
                                {getCurrencySymbol()}
                                {parseFloat(discrepancy.expectedCash).toFixed(
                                  2,
                                )}
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="text-xs text-muted-foreground">
                                Contado
                              </div>
                              <div className="text-sm font-medium">
                                {getCurrencySymbol()}
                                {parseFloat(discrepancy.countedCash).toFixed(2)}
                              </div>
                            </div>
                          </div>

                          {/* Explicación */}
                          <div className="pt-3 border-t">
                            <div className="text-xs text-muted-foreground mb-2">
                              Explicación
                            </div>
                            {discrepancy.notes ? (
                              <p className="text-sm line-clamp-3">
                                {discrepancy.notes}
                              </p>
                            ) : (
                              <div className="flex items-center">
                                <span className="text-sm text-muted-foreground italic">
                                  Sin explicación
                                </span>
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                                  ⚠️
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Acción rápida */}
                          <div className="mt-4 pt-3 border-t flex justify-between">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-8"
                              onClick={() => {
                                // TODO: Implementar modal de detalles de discrepancia
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Detalles
                            </Button>
                            <div className="text-xs text-muted-foreground flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {Math.round(
                                (new Date().getTime() -
                                  new Date(discrepancy.createdAt).getTime()) /
                                  (1000 * 60 * 60),
                              )}
                              h
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Indicador de scroll en móvil */}
                <div className="md:hidden mt-4 text-center">
                  <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="flex space-x-1">
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/30"></div>
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/50"></div>
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/70"></div>
                    </div>
                    <span>Desliza para ver más</span>
                    <div className="flex space-x-1">
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/70"></div>
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/50"></div>
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/30"></div>
                    </div>
                  </div>
                </div>

                {/* Resumen y recomendaciones */}
                {discrepanciesData.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">
                          Distribución por Tipo
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm">Faltantes</span>
                            <span className="font-medium text-destructive">
                              {discrepanciesSummary.faltantes || 0} (
                              {(
                                ((discrepanciesSummary.faltantes || 0) /
                                  (discrepanciesSummary.totalDiscrepancies ||
                                    1)) *
                                100
                              ).toFixed(0)}
                              %)
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Sobrantes</span>
                            <span className="font-medium text-amber-600">
                              {discrepanciesSummary.sobrantes || 0} (
                              {(
                                ((discrepanciesSummary.sobrantes || 0) /
                                  (discrepanciesSummary.totalDiscrepancies ||
                                    1)) *
                                100
                              ).toFixed(0)}
                              %)
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">
                          Recomendaciones
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          {(discrepanciesSummary.faltantes || 0) >
                            (discrepanciesSummary.sobrantes || 0) * 2 && (
                            <div className="text-destructive">
                              ⚠️ Revisar procedimientos de cierre - Alta tasa de
                              faltantes
                            </div>
                          )}
                          {(discrepanciesSummary.promedioFaltante || 0) >
                            50 && (
                            <div className="text-amber-600">
                              📊 Capacitar equipo - Promedio de faltante elevado
                            </div>
                          )}
                          {discrepanciesData.filter((d: any) => !d.notes)
                            .length > 0 && (
                            <div className="text-blue-600">
                              📝 Reforzar registro de explicaciones
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Mensaje de última actualización */}
                {discrepanciesData.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <div>
                        <span className="inline-flex items-center">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Última actualización:{" "}
                          {new Date().toLocaleTimeString("es-CU", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div>
                        Mostrando {discrepanciesData.length} de{" "}
                        {discrepanciesSummary.totalDiscrepancies || 0}{" "}
                        discrepancias
                      </div>
                    </div>
                  </div>
                )}

                {/* Indicador cuando no hay datos */}
                {discrepanciesData.length === 0 &&
                  !isLoading &&
                  dateRange === "today" && (
                    <div className="text-center py-12 text-muted-foreground border-t mt-6 pt-8">
                      <AlertTriangle className="h-16 w-16 mx-auto mb-4 opacity-20" />
                      <p className="text-lg font-medium">
                        No hay discrepancias hoy
                      </p>
                      <p className="text-sm mt-2">
                        Todas las operaciones de caja están correctas
                      </p>
                      <div className="mt-4 inline-flex items-center justify-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                        ✓ Estado óptimo
                      </div>
                    </div>
                  )}

                {discrepanciesData.length === 0 &&
                  !isLoading &&
                  dateRange !== "today" && (
                    <div className="text-center py-12 text-muted-foreground border-t mt-6 pt-8">
                      <AlertTriangle className="h-16 w-16 mx-auto mb-4 opacity-20" />
                      <p className="text-lg font-medium">
                        No hay discrepancias en el período seleccionado
                      </p>
                      <p className="text-sm mt-2">
                        Periodo:{" "}
                        {dateRange === "custom"
                          ? `${startDate} a ${endDate}`
                          : dateRange === "week"
                            ? "Esta semana"
                            : dateRange === "month"
                              ? "Este mes"
                              : "Hoy"}
                      </p>
                    </div>
                  )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reporte de Transferencias */}
      {reportType === "transfers" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  ></path>
                </svg>
                Reporte de Transferencias
              </div>
              <div className="flex gap-2">
                <Select
                  value={transferView}
                  onValueChange={(v: any) => setTransferView(v)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Ver por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Todas las transferencias
                    </SelectItem>
                    <SelectItem value="byBank">Agrupar por banco</SelectItem>
                    <SelectItem value="byDate">Agrupar por fecha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : transfersData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="h-12 w-12 mx-auto mb-4 opacity-20 flex items-center justify-center">
                  <svg
                    className="h-full w-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    ></path>
                  </svg>
                </div>
                <p className="text-lg">No hay transferencias registradas</p>
                <p className="text-sm">
                  Realiza ventas por transferencia para ver reportes
                </p>
              </div>
            ) : (
              <>
                {/* Estadísticas de transferencias */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Total Transferencias
                      </p>
                      <p className="text-2xl font-bold">
                        {transfersReport?.summary?.count || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Monto Total
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {getCurrencySymbol()}
                        {(transfersReport?.summary?.totalAmount || 0).toFixed(
                          2,
                        )}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Bancos Utilizados
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {Object.keys(transfersSummary.bankStats || {}).length}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        Promedio por Transferencia
                      </p>
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol()}
                        {(
                          (transfersSummary.totalAmount || 0) /
                          (transfersSummary.count || 1)
                        ).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Estadísticas por banco (Gráfico) */}
                {transfersSummary.bankStats &&
                  Object.keys(transfersSummary.bankStats).length > 0 && (
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">
                          Distribución por Banco
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(transfersSummary.bankStats).map(
                            ([bank, amount]) => {
                              const percentage = (
                                (Number(amount) /
                                  (transfersSummary.totalAmount || 1)) *
                                100
                              ).toFixed(1);
                              return (
                                // {esta sección renderiza cada banco con contención responsiva y ancho limitado al 100%}
                                <div
                                  key={bank}
                                  className="space-y-1 w-full min-w-0"
                                >
                                  <div className="flex justify-between text-sm items-center">
                                    {/* truncate evita que nombres largos de bancos rompan el layout */}
                                    <span className="truncate pr-2">
                                      {bank || "Sin banco especificado"}
                                    </span>
                                    {/* whitespace-nowrap mantiene monto y porcentaje en una sola línea */}
                                    <span className="font-medium whitespace-nowrap text-right">
                                      {getCurrencySymbol()}
                                      {Number(amount).toFixed(2)} ({percentage}
                                      %)
                                    </span>
                                  </div>

                                  {/* overflow-hidden recorta la barra si por algún motivo supera el 100% */}
                                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                                    <div
                                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                      style={{
                                        width: `${Math.min(100, Number(percentage))}%`,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {/* Búsqueda y Controles */}
                {/* {esta sección contiene la barra de herramientas de filtros para transferencias} */}
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6 p-4 bg-muted/30 rounded-lg border">
                  {/* Grupo Izquierdo: Buscador y Botón Limpiar */}
                  <div className="flex flex-1 gap-2 w-full md:w-auto">
                    <div className="relative flex-1 max-w-md">
                      <Input
                        placeholder="Buscar por Nombre, CI, Teléfono, Código o Tarjeta..."
                        value={transferSearch}
                        onChange={(e) => setTransferSearch(e.target.value)}
                        className="pl-9 pr-10"
                      />
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.3-4.3" />
                        </svg>
                      </div>

                      {/* Botón Limpiar (solo aparece si hay texto) */}
                      {transferSearch && (
                        <button
                          onClick={() => setTransferSearch("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          title="Limpiar filtro"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Grupo Derecho: Agrupación y Exportar */}
                  {/* Grupo Derecho: Agrupación y Exportar */}
                  <div className="flex gap-2 w-full md:w-auto justify-end">
                    <Select
                      value={transferView}
                      // {esta sección corrige el error de tipo convirtiendo el string genérico al tipo específico del estado}
                      onValueChange={(value: string) =>
                        setTransferView(value as "all" | "byBank" | "byDate")
                      }
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Agrupar por..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Más Recientes</SelectItem>
                        <SelectItem value="byBank">
                          Agrupar por Banco
                        </SelectItem>
                        <SelectItem value="byDate">
                          Agrupar por Fecha (Asc)
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        /* Lógica de exportar si la tienes, sino déjalo vacío o quítalo */
                      }}
                      title="Exportar a Excel"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Tabla de transferencias CON SCROLL INTERNO */}
                <div className="border rounded-lg">
                  <div className="h-[550px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Monto</TableHead>
                          <TableHead>Banco</TableHead>
                          <TableHead>Código</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>CI</TableHead>
                          <TableHead>Teléfono</TableHead>
                          <TableHead>Tarjeta Destino</TableHead>
                        </TableRow>
                      </TableHeader>
                      {/* {esta sección renderiza la tabla de transferencias sin ordenamiento local, ya que viene ordenado del backend} */}
                      <TableBody>
                        {transfersData.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="text-center py-8 text-muted-foreground"
                            >
                              No se encontraron transferencias con los filtros
                              actuales.
                            </TableCell>
                          </TableRow>
                        ) : (
                          transfersData.map((transfer: any) => (
                            <TableRow key={transfer.id}>
                              <TableCell>
                                {new Date(
                                  transfer.createdAt,
                                ).toLocaleDateString("es-CU", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </TableCell>
                              <TableCell className="font-bold text-green-600">
                                {getCurrencySymbol()}
                                {parseFloat(transfer.total || 0).toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                  {transfer.bank || "N/A"}
                                </span>
                              </TableCell>
                              <TableCell className="font-mono text-sm">
                                {transfer.transferCode || "N/A"}
                              </TableCell>
                              <TableCell>
                                {transfer.fullName ||
                                  transfer.customerName ||
                                  "N/A"}
                              </TableCell>
                              <TableCell className="font-mono">
                                {transfer.ci || transfer.customerCi || "N/A"}
                              </TableCell>
                              <TableCell>
                                {transfer.phone ||
                                  transfer.customerPhone ||
                                  "N/A"}
                              </TableCell>
                              <TableCell className="font-mono text-sm text-purple-600">
                                {transfer.destinationCard || "N/A"}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Paginación de Transferencias */}
                {transfersTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {transfersData.length} de {transfersTotal}{" "}
                      resultados
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setTransfersPage((p) => Math.max(1, p - 1))
                        }
                        disabled={transfersPage === 1}
                      >
                        Anterior
                      </Button>
                      <div className="flex items-center px-2 text-sm">
                        Página {transfersPage} de {transfersTotalPages}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setTransfersPage((p) =>
                            Math.min(transfersTotalPages, p + 1),
                          )
                        }
                        disabled={transfersPage === transfersTotalPages}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
